import React from "react";
import PropTypes from "prop-types";
import R from "ramda";
import styles from "components/documents/inbox.scss";
import grid from "components/grid.scss";
import { AutoSizer, MultiGrid, CellMeasurerCache } from "react-virtualized";
import {
  accountType,
  focusedDocumentType,
  paginationType,
  documentPropType,
  categoryType,
  documentFiltersType,
  routeType,
  routerType,
  customTabType
} from "custom-prop-types";
import { DocumentViewer } from "components/documents/documents-grid/document-viewer";
import { PaginationBar } from "components/common/pagination-bar";
import { DataGridActionsPanel } from "components/documents/documents-grid/data-grid-actions-panel";
import { ChangeDocumentTypeModal } from "components/documents/modals/change-doctype-modal";
import { TrashDocumentModal } from "components/documents/modals/trash-document-modal";
import { ShareUrlModal } from "components/documents/modals/share-url-modal";
import { DocumentFilters } from "components/documents/filters/document-filters";
import { NoDocuments } from "components/documents/documents-grid/no-documents";
import { Drawer } from "components/common/drawer";
import { Dropdown } from "components/documents/documents-grid/dropdown";
import { CellRenderer, getColumnWidth, getGridHeight } from "components/grid/grid-components";
import { GeneralLedgerActionsPanel } from "components/documents/exports/general-ledger-actions-panel";
import { DocumentsInlineTabs } from "components/documents/documents-inline-tabs";
import { ExportModal } from "components/documents/documents-grid/export-modal";

export const Inbox = (props) => {
  const rowHeight = 45;
  const visibleColumns = props.columns.filter(col => col.visible === true);
  const focusDocument = (document, style, focusParam) => {
    props.onDocumentViewed({ accountId: focusParam, document });
    props.onFocusDocument(document, style);
  };
  return (
    <div className={ styles.container }>
      <DocumentsInlineTabs
        accountSlug={ props.accountSlug }
        currentPage={ props.route.path }
        customTabs={ props.customTabs }
        onStartDeleteCustomTabMode={ props.onStartDeleteCustomTabMode }
        routeParams={ props.router.params }
      />
      <Drawer
        isDrawerMode={ props.isFiltersDrawerMode }
        onEndDrawerMode={ props.onEndFiltersDrawerMode }
      >
        <DocumentFilters { ...props } isExports={ false } />
      </Drawer>
      <div className={ grid.row }>
        <div
          className={ getGridStyle(
            "table",
            props.isDraggable,
            props.isDocumentViewerVisible,
            props.isGLCodesVisible
          ) }
        >
          <DataGridActionsPanel { ...props } />
          <div className="documents3">
            <AutoSizer disableHeight onResize={ ({ width }) => props.onSetGridWidth(width) }>
              {() => (
                <MultiGrid
                  { ...props }
                  cache={ cache }
                  cellRenderer={ ({ ...gridProps }) => <CellRenderer { ...gridProps } newKey={ gridProps.key } /> }
                  classNameBottomRightGrid={ styles["bottom-grid"] }
                  classNameTopRightGrid={ styles["top-grid"] }
                  columnCount={ visibleColumns.length }
                  columnWidth={ ({ index }) => getColumnWidth(index, props.gridWidth, visibleColumns) }
                  documents={ props.documents }
                  fixedRowCount={ 1 }
                  focusParam={ props.accountId }
                  height={ getGridHeight(props.pagination.limit, props.documents.length, rowHeight) }
                  isRowFocusable
                  isSelectable
                  key={ `${props.gridWidth}-${visibleColumns}` }
                  noContentRenderer={ () => renderEmptyRow(props.accountSlug, props.allDocumentsTotal) }
                  onFocusRow={ focusDocument }
                  rowCount={ props.documents.length + 1 }
                  rowHeight={ rowHeight }
                  selectedDocumentIds={ props.selectedDocumentIds }
                  width={ props.gridWidth }
                />
              )}
            </AutoSizer>
            <Dropdown { ...props } rowHeight={ rowHeight } />
          </div>
          <PaginationBar
            { ...props }
            limit={ props.pagination.limit }
            onChangeLimit={ props.onSetPaginationLimit }
            pageNumber={ props.pagination.pageNumber }
            totalResults={ props.pagination.totalCountFiltered }
          />
        </div>

        <div
          className={ getGridStyle(
            "viewer",
            props.isDraggable,
            props.isDocumentViewerVisible,
            props.isGLCodesVisible
          ) }
        >
          <DocumentViewer
            documents={ props.documents }
            focusedDocument={ props.focusedDocument }
            isDocumentViewerVisible={ props.isDocumentViewerVisible }
            isDraggable={ props.isDraggable }
            isGLCodesVisible={ props.isGLCodesVisible }
            onEndDraggableMode={ props.onEndDraggableMode }
            onHideDocumentViewer={ props.onHideDocumentViewer }
            onStartDraggableMode={ props.onStartDraggableMode }
          />
          <GeneralLedgerActionsPanel
            accountId={ props.accountId }
            documents={ props.documents }
            filters={ props.filters }
            glCodes={ props.glCodes }
            isGLCodesLoading={ props.isGLCodesLoading }
            isGLCodesVisible={ props.isGLCodesVisible }
            onAddIncludeRule={ props.onAddIncludeRule }
            onAddRuleError={ props.onAddRuleError }
            onLinkAccount={ props.onLinkAccount }
            onMapVendorRules={ props.onMapVendorRules }
            onRemoveIncludeRule={ props.onRemoveIncludeRule }
            preferredCurrency={ props.selectedAccount.preferredCurrency }
            setDocumentUrl={ props.onSetViewedDocumentUrl }
            setGlCode={ props.onSetGlCode }
          />
        </div>
      </div>
      <ChangeDocumentTypeModal
        accountId={ props.accountId }
        documentType={ props.focusedDocument.type }
        isChangeDocTypeVisible={ props.isChangeDocTypeVisible }
        onChangeDocType={ props.onChangeDocType }
        onHideChangeDocType={ props.onHideChangeDocType }
      />
      <ShareUrlModal
        accountId={ props.accountId }
        documentId={ props.focusedDocument.id }
        generatedShareUrl={ props.generatedShareUrl }
        isShareUrlVisible={ props.isShareUrlVisible }
        onGenerateShareUrl={ props.onGenerateShareUrl }
        onHideShareUrl={ props.onHideShareUrl }
        onSetFlashMessage={ props.onSetFlashMessage }
        shareUrl={ props.focusedDocument.shareUrl }
      />
      <TrashDocumentModal
        accountId={ props.accountId }
        focusedDocument={ props.focusedDocument }
        isTrashDocumentVisible={ props.isTrashDocumentVisible }
        onHideTrashDocument={ props.onHideTrashDocument }
        onTrashDocument={ props.onTrashDocument }
      />
      <ExportModal
        accountId={ props.accountId }
        accountSlug={ props.accountSlug }
        categories={ props.categories }
        glCodes={ props.glCodes }
        isExportMode={ props.isExportMode }
        onClose={ props.onEndExportMode }
        onExportAll={ props.onExportAll }
        onExportSelected={ props.onExportSelected }
        onLinkAccount={ props.onLinkAccount }
        selectedDocumentIds={ props.selectedDocumentIds }
      />
    </div>
  );
};

Inbox.defaultProps = {
  customTabs: [],
  generatedShareUrl: "",
  glCodes: []
};

Inbox.propTypes = {
  accountId: PropTypes.string.isRequired,
  accountSlug: PropTypes.string.isRequired,
  allDocumentsTotal: PropTypes.number.isRequired,
  categories: PropTypes.arrayOf(categoryType).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    sortable: PropTypes.bool,
    visible: PropTypes.bool
  })).isRequired,
  customTabs: PropTypes.arrayOf(customTabType),
  documents: PropTypes.arrayOf(documentPropType).isRequired,
  filters: documentFiltersType.isRequired,
  focusedDocument: focusedDocumentType.isRequired,
  generatedShareUrl: PropTypes.string,
  glCodes: PropTypes.arrayOf(categoryType),
  gridWidth: PropTypes.number.isRequired,
  isChangeDocTypeVisible: PropTypes.bool.isRequired,
  isDocumentViewerVisible: PropTypes.bool.isRequired,
  isDraggable: PropTypes.bool.isRequired,
  isExportMode: PropTypes.bool.isRequired,
  isFiltersDrawerMode: PropTypes.bool.isRequired,
  isGLCodesLoading: PropTypes.bool.isRequired,
  isGLCodesVisible: PropTypes.bool.isRequired,
  isShareUrlVisible: PropTypes.bool.isRequired,
  isTrashDocumentVisible: PropTypes.bool.isRequired,
  onAddIncludeRule: PropTypes.func.isRequired,
  onAddRuleError: PropTypes.func.isRequired,
  onChangeDocType: PropTypes.func.isRequired,
  onDocumentViewed: PropTypes.func.isRequired,
  onEndDraggableMode: PropTypes.func.isRequired,
  onEndExportMode: PropTypes.func.isRequired,
  onEndFiltersDrawerMode: PropTypes.func.isRequired,
  onExportAll: PropTypes.func.isRequired,
  onExportSelected: PropTypes.func.isRequired,
  onFocusDocument: PropTypes.func.isRequired,
  onGenerateShareUrl: PropTypes.func.isRequired,
  onHideChangeDocType: PropTypes.func.isRequired,
  onHideDocumentViewer: PropTypes.func.isRequired,
  onHideShareUrl: PropTypes.func.isRequired,
  onHideTrashDocument: PropTypes.func.isRequired,
  onLinkAccount: PropTypes.func.isRequired,
  onMapVendorRules: PropTypes.func.isRequired,
  onRemoveIncludeRule: PropTypes.func.isRequired,
  onSetFlashMessage: PropTypes.func.isRequired,
  onSetGlCode: PropTypes.func.isRequired,
  onSetGridWidth: PropTypes.func.isRequired,
  onSetPaginationLimit: PropTypes.func.isRequired,
  onSetViewedDocumentUrl: PropTypes.func.isRequired,
  onStartDeleteCustomTabMode: PropTypes.func.isRequired,
  onStartDraggableMode: PropTypes.func.isRequired,
  onTrashDocument: PropTypes.func.isRequired,
  pagination: paginationType.isRequired,
  route: routeType.isRequired,
  router: routerType.isRequired,
  selectedAccount: accountType.isRequired,
  selectedDocumentIds: PropTypes.arrayOf(PropTypes.string).isRequired
};

Inbox.defaultProps = {
  endDate: null,
  exportType: null,
  startDate: null
};

export const getGridStyle = (type, isDraggable, isDocumentViewerVisible, isGLCodesVisible) => {
  if (type === "table") {
    return getTableStyle(isDraggable, isDocumentViewerVisible, isGLCodesVisible);
  }
  return getSideBarStyle(isDraggable, isDocumentViewerVisible, isGLCodesVisible);
};

const getTableStyle = (isDraggable, isDocumentViewerVisible, isGLCodesVisible) => {
  if (isDocumentViewerVisible && !isDraggable) {
    return grid["col-md-8"];
  }
  if (isGLCodesVisible) {
    return `${grid["col-md-8"]} ${styles["gl-grid"]}`;
  }
  return grid["col-md-12"];
};

const getSideBarStyle = (isDraggable, isDocumentViewerVisible, isGLCodesVisible) => {
  if (isDocumentViewerVisible && !isDraggable) {
    return grid["col-md-4"];
  }
  if (isGLCodesVisible) {
    return `${grid["col-md-4"]} ${styles["gl-panel"]}`;
  }
  return "";
};


export const getRow = (documents, selectedDocumentIds, selectedAccount) => {
  const cleanData = documents.map(document => R.merge(
    document,
    {
      isSelected: selectedDocumentIds.includes(document.id),
      categories: JSON.stringify(document.categories),
      defaultCurrency: selectedAccount.preferredCurrency,
      downloadUrl: R.path(["attachment", "url"], document),
      paymentType: JSON.stringify(document.paymentType)
    }
  ));
  return (index) => cleanData[index];
};

export const renderEmptyRow = (accountSlug, allDocumentsCount) => (
  <NoDocuments accountSlug={ accountSlug } totalCount={ allDocumentsCount } />
);

const cache = new CellMeasurerCache({
  defaultWidth: 75,
  minWidth: 75,
  fixedHeight: true
});



// WEBPACK FOOTER //
// ./src/js/components/documents/inbox.jsx